<script lang="ts">
    import { get } from 'svelte/store';
    import { location, push } from 'svelte-spa-router';

    import { FIFA_CARD_HEADER_BG, FIFA_LOGO } from '../constants/imgList';
    import { LOGIN_STATE_CK, PAGE_CLICK_CK } from '../enums/track';
    import { isLogin } from '../store/index';
    import { resetLoginFlow } from '../store/loginFlow';
    import { logout } from '../utils/passport';
    import { trackEvent } from '../utils/track';

    import CommonPopup from './CommonPopup.svelte';

    import webp from '@/actions/webp';
    import { RULES_CONTENT } from '@/constants/mxCreditCardMsiRulesContent';

    export let headerBg: string = FIFA_CARD_HEADER_BG;
    export let heroVariant: 'default' | 'msi' = 'default';
    export let heroLines: string[] = [
        'VIVE LA',
        'EXPERIENCIA',
        'COMPLETA'
    ];

    let isShowRulesPopup = false;

    async function handleLog() {
        trackEvent(LOGIN_STATE_CK, {
            button_name: 'log_out'
        });

        try {
            await logout();
            resetLoginFlow();

            if (get(location) !== '/') {
                await push('/');
            }
        } catch (error) {
            console.error('[LoginHeader logout]', error);
        }
    }

    function handleShowRules() {
        isShowRulesPopup = true;
        trackEvent(PAGE_CLICK_CK, {
            button_name: 'rules'
        });
    }

    function handleCloseRulesPopup() {
        isShowRulesPopup = false;
        trackEvent(PAGE_CLICK_CK, {
            button_name: 'rules_close'
        });
    }
</script>

<div class="login-header-shell" class:login-header-shell--msi={heroVariant === 'msi'}>
    <div class="login-header__main" use:webp={headerBg}>
        <div
            class="login-header__bar"
            class:login-header__bar--logged-in={$isLogin}
        >
            {#if $isLogin}
                <div class="login-header__logo login-header__logo--left">
                    <img use:webp src={FIFA_LOGO} alt="DiDi Card" />
                </div>
                <div class="login-header__bar-spacer"></div>
                <div class="login-header__bar-actions">
                    <button
                        type="button"
                        class="login-header__logout"
                        on:click={handleLog}
                    >
                        Cerrar sesión
                    </button>
                    <span class="login-header__bar-divider" aria-hidden="true">|</span>
                    <button
                        type="button"
                        class="login-header__rules"
                        on:click={handleShowRules}
                    >
                        T&C
                    </button>
                </div>
            {:else}
                <div class="login-header__logo login-header__logo--center">
                    <img use:webp src={FIFA_LOGO} alt="DiDi Card" />
                </div>
                <div class="login-header__bar-actions login-header__bar-actions--overlay">
                    <button
                        type="button"
                        class="login-header__rules"
                        on:click={handleShowRules}
                    >
                        T&C
                    </button>
                </div>
            {/if}
        </div>

        <div class="login-header__hero" aria-hidden="true">
            {#if heroVariant === 'msi'}
                <p class="login-header__hero-line login-header__hero-line--1">{heroLines[0]}</p>
                {#if heroLines[1]}
                    <p class="login-header__hero-line login-header__hero-line--3">{heroLines[1]}</p>
                {/if}
            {:else}
                <p class="login-header__hero-line login-header__hero-line--1">{heroLines[0]}</p>
                <p class="login-header__hero-line login-header__hero-line--2">
                    {heroLines[1]}
                </p>
                <p class="login-header__hero-line login-header__hero-line--3">{heroLines[2]}</p>
            {/if}
        </div>
    </div>
</div>

<CommonPopup
    isOpen={isShowRulesPopup}
    content={RULES_CONTENT}
    title="Reglas"
    on:close={handleCloseRulesPopup}
/>

<style lang="scss">
    .login-header-shell {
        width: 100%;
        max-width: 7.5rem;
        margin-left: auto;
        margin-right: auto;
    }

    .login-header__main {
        box-sizing: border-box;
        width: 100%;
        max-width: 7.5rem;
        margin-left: auto;
        margin-right: auto;
        height: 7.17rem;
        padding: 0.35rem 0.4rem 0;
        background-repeat: no-repeat;
        background-size: cover;
        background-position: center top;
        position: relative;
        overflow: hidden;
    }

    .login-header__bar {
        position: relative;
        z-index: 2;
        width: 100%;
        display: flex;
        margin-top: 0.4rem;
        align-items: center;
        height: 0.37rem;
        line-height: 0.36rem;
    }

    .login-header__bar--logged-in {
        box-sizing: border-box;
        width: calc(100% - 0.2rem);
        margin-left: 0.2rem;
        padding-right: 0.1rem;
    }

    .login-header__bar-spacer {
        flex: 1;
        height: 100%;
    }

    .login-header__bar-actions {
        display: flex;
        flex-direction: row;
        align-items: center;
        flex-shrink: 0;
        height: 0.37rem;
    }

    .login-header__bar-actions--overlay {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 3;
    }

    .login-header__bar-divider {
        color: #ffffff;
        font-family: DiDiSans-Pro-Regular;
        font-size: 0.24rem;
        line-height: 0.37rem;
        letter-spacing: 0;
        padding: 0 0.08rem;
        opacity: 0.8;
    }

    .login-header__logo {
        display: flex;
        align-items: center;
        height: 0.3rem;

        img {
            display: block;
            height: 0.3rem;
            width: auto;
        }
    }

    .login-header__logo--center {
        width: 100%;
        justify-content: center;
    }

    .login-header__logout,
    .login-header__rules {
        box-sizing: border-box;
        height: 0.37rem;
        border: none;
        outline: none;
        box-shadow: none;
        background: transparent;
        -webkit-appearance: none;
        appearance: none;
        border-radius: 0;
        padding: 0 0.12rem;
        font-family: DiDiSans-Pro-Medium;
        color: #ffffff;
        font-size: 0.28rem;
        font-weight: 500;
        line-height: 0.37rem;
        text-align: center;
        cursor: pointer;
        white-space: nowrap;
    }

    .login-header__logout {
        min-width: 1.18rem;
    }

    /* Card_2 底图 + HTML 渐变字，实现 Card_1 效果；750 设计稿 px / 100 => rem */
    .login-header__hero {
        position: absolute;
        inset: 0;
        z-index: 1;
        pointer-events: none;
        overflow: visible;
    }

    @keyframes login-header-hero-gradient {
        0% {
            background-position: 0% 50%;
        }

        50% {
            background-position: 100% 50%;
        }

        100% {
            background-position: 0% 50%;
        }
    }

    /* 正常模式：适度色差 + 略加长渐变条，扫光可见但不抢眼 */

    .login-header__hero-line {
        position: absolute;
        box-sizing: content-box;
        margin: 0;
        padding: 0 0.08rem 0 0;
        width: fit-content;
        opacity: 0.95;
        font-family: DiDiSans-Pro-ExtraBold, DiDiSans-Pro-Bold, sans-serif;
        font-weight: 800;
        text-transform: uppercase;
        font-feature-settings: 'kern' on;
        white-space: nowrap;
        overflow: visible;
        background-clip: text;
        -webkit-background-clip: text;
        color: transparent;
        -webkit-text-fill-color: transparent;
        filter: drop-shadow(0.03rem 0.04rem 0 rgba(21, 29, 12, 0.8));
        background-size: 345% 100%;
        background-repeat: repeat;
        animation: login-header-hero-gradient 3.25s ease-in-out infinite;
        will-change: background-position;
    }

    .login-header__hero-line--1 {
        left: 1.61rem;
        top: 2.12rem;
        height: 1rem;
        font-size: 1.04rem;
        line-height: 1rem;
        letter-spacing: 0.02em;
        background-image: linear-gradient(
            99deg,
            #92b060 0%,
            #ffffff 14%,
            #dff898 30%,
            #ffffff 46%,
            #96b058 60%,
            #ecfab8 74%,
            #ffffff 88%,
            #92b060 100%
        );
    }

    .login-header__hero-line--2 {
        left: 1.6rem;
        top: 3.2rem;
        height: 0.64rem;
        font-size: 0.64rem;
        line-height: 0.64rem;
        letter-spacing: 0;
        animation-delay: 0.12s;
        background-image: linear-gradient(
            95deg,
            #92b060 0%,
            #ffffff 16%,
            #d8f098 34%,
            #ffffff 50%,
            #88a454 66%,
            #e8f6b0 82%,
            #92b060 100%
        );
    }

    .login-header__hero-line--3 {
        left: 1.52rem;
        top: 3.92rem;
        height: 0.8rem;
        font-size: 0.8rem;
        line-height: 0.8rem;
        letter-spacing: 0;
        animation-delay: 0.24s;
        background-image: linear-gradient(
            97deg,
            #92b060 0%,
            #ffffff 14%,
            #daf090 32%,
            #ffffff 48%,
            #8ea850 64%,
            #eef8b8 80%,
            #ffffff 92%,
            #92b060 100%
        );
    }

    /* msi：750 稿 116px / 76px，背景高 820px */
    .login-header-shell--msi {
        .login-header__main {
            height: 8.2rem;
        }

        .login-header__hero-line--1 {
            top: 2.24rem;
            height: 1.16rem;
            font-size: 1.16rem;
            line-height: 1.16rem;
        }

        .login-header__hero-line--3 {
            top: 3.64rem;
            height: 0.76rem;
            font-size: 0.76rem;
            line-height: 0.76rem;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .login-header__hero-line {
            animation: none;
            background-size: 100% 100%;
            will-change: auto;
        }

        .login-header__hero-line--1 {
            background-image: linear-gradient(
                99deg,
                #ffffff -3%,
                #e1fcb1cc 23%,
                #ffffff 80%,
                #b4cc89 114%
            );
        }

        .login-header__hero-line--2 {
            background-image: linear-gradient(
                95deg,
                #ffffff -3%,
                #ffffff 47%,
                #b4cc89cc 99%
            );
        }

        .login-header__hero-line--3 {
            background-image: linear-gradient(
                97deg,
                #ffffff -3%,
                #d3e8acd9 56%,
                #ffffff 81%,
                #b4cc89 114%
            );
        }
    }
</style>
